import type { OrderReportResponse, ReportGuidanceClassification } from '@/types/OrderReportTypes'

/** Classifications that warrant the inconclusive-guidance notice banner. */
const INCONCLUSIVE_CLASSIFICATIONS: ReadonlySet<string> = new Set(['uncertain', 'inconclusive'])

/**
 * Reads the CGE guidance envelope status for a report.
 *
 * Prefers the top-level `guidance_status` field (which the API can surface even
 * when the full guidance body is withheld for non-final statuses) and falls
 * back to `guidance.status` when the body is present (e.g. finalized guidance).
 * Returns '' when no status is available.
 */
export function guidanceStatusOf(report: OrderReportResponse): string {
  const flat = (report as { guidance_status?: unknown }).guidance_status
  if (typeof flat === 'string' && flat.trim() !== '') return flat.trim().toLowerCase()
  const guidance = report.guidance as { status?: unknown } | null | undefined
  const status = guidance?.status
  return typeof status === 'string' ? status.trim().toLowerCase() : ''
}

/** True when the report carries a finalized guidance envelope. */
export function hasFinalGuidance(report: OrderReportResponse): boolean {
  return guidanceStatusOf(report) === 'final'
}

/**
 * True when the report's guidance was rejected (`status === 'rejected'`). A
 * rejected envelope means no guidance is coming for this result, so the order
 * routes to the PDF results with a notice instead of the Guidance page.
 */
export function isRejectedGuidance(report: OrderReportResponse): boolean {
  return guidanceStatusOf(report) === 'rejected'
}

/**
 * Reads the report-level `guidance.classification` block, when present.
 *
 * The classification is net-new on the guidance envelope and may be absent on
 * older API responses or non-final guidance. Returns null when missing.
 */
export function guidanceClassificationOf(report: OrderReportResponse): ReportGuidanceClassification | null {
  const guidance = report.guidance as { classification?: unknown } | null | undefined
  const classification = guidance?.classification
  if (!classification || typeof classification !== 'object') return null
  return classification as ReportGuidanceClassification
}

/**
 * The `classificationExplanation` to surface in a banner, but only when the
 * report's classification is one the UI treats as inconclusive (`uncertain` /
 * `inconclusive`). Returns null when the classification is absent, is a
 * conclusive outcome, or carries no explanation text.
 */
export function inconclusiveClassificationExplanation(report: OrderReportResponse): string | null {
  const classification = guidanceClassificationOf(report)
  const reportClassification = classification?.reportClassification
  if (typeof reportClassification !== 'string') return null
  if (!INCONCLUSIVE_CLASSIFICATIONS.has(reportClassification.trim().toLowerCase())) return null
  const explanation = classification?.classificationExplanation
  if (typeof explanation !== 'string' || explanation.trim() === '') return null
  return explanation.trim()
}

/** Newest-first comparator: generated_at, then version, then id (stable). */
function byRecencyDesc(a: OrderReportResponse, b: OrderReportResponse): number {
  const aMs = a.generated_at ? new Date(a.generated_at).getTime() : NaN
  const bMs = b.generated_at ? new Date(b.generated_at).getTime() : NaN
  const aSafe = Number.isFinite(aMs) ? aMs : -1
  const bSafe = Number.isFinite(bMs) ? bMs : -1
  if (aSafe !== bSafe) return bSafe - aSafe

  const aV = a.version ?? -1
  const bV = b.version ?? -1
  if (aV !== bV) return bV - aV

  return a.id.localeCompare(b.id)
}

/**
 * Pick the most recent report whose guidance has been finalized
 * (`status === 'final'`). Newest wins by `generated_at`, then by `version`, then
 * by id as a stable tiebreak. Returns null when no report has final guidance so
 * the page can show an empty state.
 */
export function selectFinalGuidanceReport(reports: ReadonlyArray<OrderReportResponse>): OrderReportResponse | null {
  const finals = reports.filter(hasFinalGuidance)
  if (finals.length === 0) return null
  return [...finals].sort(byRecencyDesc)[0]
}

/**
 * Pick the most recent report overall (regardless of guidance status), used to
 * determine the order's *current* guidance disposition. Returns null for an
 * empty list.
 */
export function selectLatestReport(reports: ReadonlyArray<OrderReportResponse>): OrderReportResponse | null {
  if (reports.length === 0) return null
  return [...reports].sort(byRecencyDesc)[0]
}
